import React, { useCallback, useRef, useContext, type FC } from 'react';
import { FormattedMessage } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { SpotlightTarget } from '@atlaskit/onboarding';
import {
	FlyoutMenuItem,
	FlyoutMenuItemContent,
	FlyoutMenuItemTrigger,
} from '@atlaskit/navigation-system/side-nav-items/flyout-menu-item';

import { preloadSpacesList } from '@confluence/space-utils/entry-points/preloadSpacesList.ts';
import { usePageSpaceKey } from '@confluence/page-context';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { createLazyCallbackHook } from '@confluence/loadable/entry-points/lazy-callback.ts';
import { useRouteDataRef, useRouteName } from '@confluence/route-manager/entry-points/RouteState.ts';
import { SpacesDropdownLoader } from '@confluence/spaces-dropdown/entry-points/SpacesDropdown.ts';
import { APP_NAV_SPACES_DROPDOWN_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout.ts';
import { SPACE_DIRECTORY } from '@confluence/named-routes/entry-points/routes.ts';
import { useFlyout } from '@confluence/flyout-store';
import { expVal } from '@confluence/feature-experiments';

import { i18n } from '../globalNavigationTranslations.ts';
import { GlobeIconComponent, SpacesIconComponent } from '../GlobalNavigationIcons.tsx';

import type { GlobalItemProps } from './globalItemProps.ts';
import { useGlobalItemVisibility } from './useGlobalItemVisibility.ts';

const MENU_ID = 'spaces';

const useLazyClickAnalytics = createLazyCallbackHook(
	async () =>
		(await import(/* webpackChunkName: "loadable-analyticsCallbacks" */ '../analyticsCallbacks.ts'))
			.fireSpacesClickedAnalytics,
);

export const SpacesItem: FC<GlobalItemProps> = ({ isHidden, peekingId, setPeekingId }) => {
	const { isFlyoutOpen, openFlyout, closeFlyout } = useFlyout(MENU_ID);

	const routeDataRef = useRouteDataRef();
	const [spaceKey] = usePageSpaceKey();
	const selectedSpaceKey: string | undefined = spaceKey;
	const { isLicensed } = useSessionData();

	const isMounted = useRef(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const fireClickAnalytics = useLazyClickAnalytics(createAnalyticsEvent, routeDataRef);
	const experienceTracker = useContext(ExperienceTrackerContext);

	const abortExperienceTracker = useCallback(() => {
		experienceTracker.abort({
			name: APP_NAV_SPACES_DROPDOWN_EXPERIENCE,
			reason: 'Primary dropdown menu: closed by user',
			attributes: {
				navVersion: '4',
				dropdownType: 'spaces',
			},
		});
	}, [experienceTracker]);

	const startExperienceTracker = useCallback(() => {
		experienceTracker.start({
			name: APP_NAV_SPACES_DROPDOWN_EXPERIENCE,
			timeout: ExperienceTimeout.NAVIGATION_LOAD,
			attributes: {
				navVersion: '4',
				dropdownType: 'spaces',
			},
		});
	}, [experienceTracker]);

	const onOpenChange = useCallback(
		(newIsOpen: boolean) => {
			if (!isMounted.current) {
				isMounted.current = true;
				return;
			}
			if (!newIsOpen) {
				abortExperienceTracker();
				void fireClickAnalytics(false);
				isFlyoutOpen && closeFlyout();
			} else {
				openFlyout();
			}
		},
		[abortExperienceTracker, fireClickAnalytics, isFlyoutOpen, openFlyout, closeFlyout],
	);

	const onClick = useCallback(() => {
		if (!isFlyoutOpen) {
			openFlyout();
			startExperienceTracker();
			void fireClickAnalytics(true);
		} else {
			closeFlyout();
		}
	}, [fireClickAnalytics, isFlyoutOpen, startExperienceTracker, openFlyout, closeFlyout]);

	const onMouseEnter = useCallback(() => {
		if (!isFlyoutOpen) {
			void SpacesDropdownLoader.preload();
			if (selectedSpaceKey) {
				void preloadSpacesList({
					isLicensed,
					selectedSpaceKey,
				});
			}
		}
	}, [isFlyoutOpen, isLicensed, selectedSpaceKey]);

	const isSelected = useRouteName(isOnSpacesRoute);
	const shouldHide = useGlobalItemVisibility(
		MENU_ID,
		isSelected || isFlyoutOpen,
		isHidden,
		peekingId,
		setPeekingId,
	);

	if (shouldHide) {
		return null;
	}

	return (
		<>
			<div onMouseEnter={onMouseEnter} onFocus={onMouseEnter}>
				<SpotlightTarget name="spaces-item-spotlight">
					<FlyoutMenuItem onOpenChange={onOpenChange} isOpen={isFlyoutOpen}>
						<FlyoutMenuItemTrigger
							elemBefore={
								expVal('confluence_space_icon_update', 'isEnabled', false)
									? SpacesIconComponent
									: GlobeIconComponent
							}
							onClick={onClick}
							isSelected={isSelected}
						>
							<FormattedMessage {...i18n.spaces} />
						</FlyoutMenuItemTrigger>
						<FlyoutMenuItemContent onClose={() => isFlyoutOpen && closeFlyout()} autoFocus={false}>
							<Box xcss={fixFocus}>
								<SpacesDropdownLoader isNav4Enabled />
							</Box>
						</FlyoutMenuItemContent>
					</FlyoutMenuItem>
				</SpotlightTarget>
			</div>
		</>
	);
};

const fixFocus = xcss({
	//@ts-expect-error styling is applying but property does not exist for SafeCSSObject
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	a: {
		':focus-visible': {
			outlineOffset: `${token('space.negative.025')}`,
		},
	},
});

const isOnSpacesRoute = {
	selector: (routeName: string | undefined) => routeName === SPACE_DIRECTORY.name,
};
